import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const FALLBACK = [
  { id: "gb-highgate", name: "Highgate Cemetery", city: "London", state: "England", country: "United Kingdom", established: "1839", records: "170,000+" },
  { id: "fr-perelachaise", name: "Père Lachaise", city: "Paris", state: "Île-de-France", country: "France", established: "1804", records: "1,000,000+" },
  { id: "us-arlington", name: "Arlington National Cemetery", city: "Arlington", state: "Virginia", country: "United States", established: "1864", records: "400,000+" },
  { id: "at-vienna-zentral", name: "Zentralfriedhof (Vienna Central Cemetery)", city: "Vienna", state: "Vienna", country: "Austria", established: "1874", records: "3,000,000+" },
  { id: "ie-glasnevin", name: "Glasnevin Cemetery", city: "Dublin", state: "Leinster", country: "Ireland", established: "1832", records: "1,500,000+" },
  { id: "au-rookwood", name: "Rookwood Necropolis", city: "Sydney", state: "New South Wales", country: "Australia", established: "1867", records: "1,000,000+" },
];

export default function CemeteriesPreview() {
  const [items, setItems] = useState(FALLBACK);
  const [total, setTotal] = useState(0);
  const [countries, setCountries] = useState(0);

  useEffect(() => {
    axios.get(`${API}/cemeteries/index`).then((r) => {
      setTotal(r.data.total);
      setCountries(r.data.countries.length);
    }).catch(() => {});
    axios.get(`${API}/cemeteries`, { params: { limit: 6 } }).then((r) => {
      if (r.data.results && r.data.results.length > 0) setItems(r.data.results);
    }).catch(() => {});
  }, []);

  return (
    <section
      data-testid="cemeteries-preview-section"
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[color:var(--ink-line)]"
    >
      {/* Header */}
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 items-end">
        <div className="col-span-12 md:col-span-7">
          <div className="overline text-[color:var(--ink-soft)] mb-4">§ Cemeteries · A Cross-border Directory</div>
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(40px, 5.5vw, 88px)" }}
          >
            Where a name{" "}
            <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
              is kept in stone.
            </span>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 md:pl-16">
          <p className="text-[15px] leading-[1.65] text-[color:var(--ink-soft)] max-w-md">
            {total || items.length}+ cemeteries across {countries || 15} countries — searchable by
            country, state and city, each linked to its authoritative public record.
          </p>
          <div className="mt-6 flex gap-3">
            <Link
              to="/cemeteries"
              className="btn-rust"
              data-testid="cemeteries-preview-cta"
            >
              <Search size={14} /> Search the directory
            </Link>
          </div>
        </div>
      </div>

      {/* Preview ledger */}
      <div className="rule-strong" />
      {items.slice(0, 6).map((c, i) => (
        <motion.div
          key={c.id}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: i * 0.04, ease }}
          className="grid grid-cols-12 gap-3 md:gap-6 py-5 md:py-7 border-b border-[color:var(--ink-line)] items-baseline"
          data-testid={`cem-preview-${c.id}`}
        >
          <div className="col-span-2 md:col-span-1 numeric-index text-[color:var(--ink-soft)]">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="col-span-10 md:col-span-5">
            <div
              className="font-serif-display leading-[1.05]"
              style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
            >
              {c.name}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[12px] text-[color:var(--ink-soft)]">
              <MapPin size={12} />
              <span>{c.city} · {c.state} · {c.country}</span>
            </div>
          </div>
          <div className="col-span-6 md:col-span-3 numeric-index text-[color:var(--ink-soft)]">
            Est. {c.established}
          </div>
          <div className="col-span-4 md:col-span-2 numeric-index text-[color:var(--ink-soft)]">
            {c.records}
          </div>
          <div className="col-span-2 md:col-span-1 flex justify-end">
            <Link to="/cemeteries" data-testid={`cem-preview-arrow-${c.id}`}>
              <ArrowUpRight size={20} className="hover:text-[color:var(--rust)]" />
            </Link>
          </div>
        </motion.div>
      ))}

      {/* View all */}
      <div className="mt-8 flex items-center justify-between">
        <span className="numeric-index text-[color:var(--ink-soft)]">
          Showing 6 of {total || "many"} · updated weekly
        </span>
        <Link to="/cemeteries" className="link-underline text-[14px]" data-testid="cem-preview-view-all">
          View the full directory →
        </Link>
      </div>
    </section>
  );
}
